
import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useCommunication } from './CommunicationWrapper';


type ClassScheduleItem = {
    start: number;
    end: number;
    course: string;
}

type ClassSchedules = {
    [day in 0 | 1 | 2 | 3 | 4 | 5 | 6]: ClassScheduleItem[];
};

// Normalize schedule period to ensure start < end, skip if start === end
const normalizeSchedulePeriod = (period: { start: number; end: number; day: number }) => {
    // If times are equal, skip this period
    if (period.start === period.end) {
        return null;
    }

    // Ensure start is always less than end
    const start = Math.min(period.start, period.end);
    const end = Math.max(period.start, period.end);

    return {
        start,
        end,
        day: period.day
    };
};

export const useCourses = () => {

    const { useRerenderListener } = useCommunication();
    const { options, trigger } = useRerenderListener(["card_ids", "card_.*"]);


    const generateSchedule = useCallback(() => {


        const output: any = {};
        (options?.["card_ids"] || []).forEach((id: string) => {
            const schedule = options?.["card_" + id]?.schedule || [];
            schedule.forEach((item: any) => {
                const normalized = normalizeSchedulePeriod(item);
                // Skip if period is invalid (start === end)
                if (!normalized) return;

                if (!output[normalized.day]) output[normalized.day] = [];
                output[normalized.day].push({
                    "start": normalized.start,
                    "end": normalized.end,
                    "course": id
                });
            });
        });
        return output;
    }, [trigger]);

    const schedule: ClassSchedules = useMemo(() => generateSchedule(), [trigger]);
    const courseData = useMemo(() => {
        const output: any = {};
        (options?.["card_ids"] || []).forEach((id: string, index: number) => {
            const color = options["theme_enabled"] ? options?.["card_" + id]?.color : (options?.["card_" + id]?.defaultColor || "gray");
            const img = options["theme_enabled"] ? options?.["card_" + id]?.img : "";
            output[id] = { ...options?.["card_" + id] || {}, "color": color, "img": img };
        });
        return output;
    }, [trigger]);

    const orderedCourseIds = useMemo(() => {
        return options?.["card_ids"] || [];
    }, [trigger]);

    const visibleCourseIds = useMemo(() => {
        return orderedCourseIds.filter((id: string) => !courseData[id]?.hidden);
    }, [orderedCourseIds, courseData]);

    const hiddenCourses = useMemo(() => {
        return new Set(Object.keys(courseData).filter((key: string) => courseData[key].hidden === true));
    }, [courseData]);


    return {
        schedule,
        courseData,
        hiddenCourses,
        orderedCourseIds,
        visibleCourseIds
    }
}
